import { LAMP_MULT, type LampTier } from "./daily.js";
import type { ItemId } from "./items.js";
import { SKILL_IDS, SKILLS, type SkillId, type SkillState } from "./skills.js";

export interface AchievementDef {
  id: string;
  name: string;
  description: string;
  lamp: LampTier;
}

export interface AchievementChapter {
  id: string;
  title: string;
  /** Icon key for Hud iconSvg. */
  glyph: string;
  achievements: AchievementDef[];
}

export const ACHIEVEMENTS: AchievementDef[] = [
  ...SKILL_IDS.flatMap((skill) =>
    ([10, 20, 30, 40, 50, 60, 70, 80, 90, 99] as const).map((level) => ({
      id: `skill-${skill}-${level}`,
      name: `Level ${level}`,
      description: `Reach ${title(skill)} level ${level}.`,
      lamp: lampForLevel(level),
    })),
  ),
  { id: "gather-50", name: "Busy Paws", description: "Gather 50 items.", lamp: "small" },
  { id: "gather-250", name: "Dedicated Digger", description: "Gather 250 items.", lamp: "medium" },
  { id: "craft-25", name: "Treat Apprentice", description: "Craft 25 items.", lamp: "small" },
  { id: "craft-100", name: "Forge Regular", description: "Craft 100 items.", lamp: "medium" },
  { id: "visit-hub", name: "Town Dog", description: "Visit the Plaza.", lamp: "small" },
  { id: "visit-digPark", name: "Park Explorer", description: "Visit Dig Park.", lamp: "small" },
  { id: "visit-fetchWoods", name: "Woods Walker", description: "Visit Fetch Woods.", lamp: "small" },
  { id: "visit-kennel", name: "Homebody", description: "Visit a kennel.", lamp: "small" },
];

const SKILL_GLYPH: Record<SkillId, string> = {
  digging: "shovel",
  fetching: "ball",
  sniffing: "berry",
  begging: "sit",
  toycraft: "hammer",
  treatcraft: "treat",
};

export function achievementChapters(): AchievementChapter[] {
  const skills = SKILL_IDS.map((skill) => ({
    id: skill,
    title: title(skill),
    glyph: SKILL_GLYPH[skill],
    achievements: ACHIEVEMENTS.filter((a) => a.id.startsWith(`skill-${skill}-`)),
  }));
  return [
    ...skills,
    {
      id: "feats",
      title: "Feats",
      glyph: "star",
      achievements: ACHIEVEMENTS.filter((a) => !a.id.startsWith("skill-")),
    },
  ];
}

export interface AchievementProgressInput {
  skills: Record<SkillId, SkillState>;
  visitedAreas: string[];
  lifetimeGathered?: Partial<Record<ItemId, number>>;
  lifetimeCrafted?: Partial<Record<ItemId, number>>;
}

export function isAchievementMet(input: AchievementProgressInput, id: string): boolean {
  const skillMatch = /^skill-(digging|fetching|sniffing|begging|toycraft|treatcraft)-(\d+)$/.exec(id);
  if (skillMatch) {
    return (input.skills[skillMatch[1] as SkillId]?.level ?? 0) >= Number(skillMatch[2]);
  }
  const gathered = Object.values(input.lifetimeGathered ?? {}).reduce((a, b) => a + (b ?? 0), 0);
  const crafted = Object.values(input.lifetimeCrafted ?? {}).reduce((a, b) => a + (b ?? 0), 0);
  if (id === "gather-50") {
    return gathered >= 50;
  }
  if (id === "gather-250") {
    return gathered >= 250;
  }
  if (id === "craft-25") {
    return crafted >= 25;
  }
  if (id === "craft-100") {
    return crafted >= 100;
  }
  if (id.startsWith("visit-")) {
    return input.visitedAreas.includes(id.slice(6));
  }
  return false;
}

export function lampRewardCopy(tier: LampTier): string {
  const amount =
    tier === "huge" ? "A huge" : tier === "large" ? "A large" : tier === "medium" ? "A medium" : "A small";
  return `${amount} amount of XP to a skill of your choice`;
}

function title(skill: SkillId): string {
  return SKILLS[skill]?.name ?? skill;
}

function lampForLevel(level: number): LampTier {
  if (level >= 99) {
    return "huge";
  }
  if (level >= 70) {
    return "large";
  }
  if (level >= 40) {
    return "medium";
  }
  return "small";
}

export function achievementById(id: string): AchievementDef | undefined {
  return ACHIEVEMENTS.find((entry) => entry.id === id);
}

export { LAMP_MULT };
